import { useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, LinearProgress, MenuItem, TextField } from '@mui/material'

import { config } from '../../config'
import { Pocket } from '../../utils/dexie/db'
import { AppStateContext } from '../../contexts'
import Dialog from '../../components/common/dialog'
import { FormError } from '../../types/common.types'
import { UserSessionType } from '../../types/user.types'
import DocumentIcon from '../../assets/icons/document-icon.svg'
import Dropzone from '../../components/upload-document-process/dropzone'
import { baseButtonWhiteBg, textFieldWhiteBg } from '../../theme/custom-styles'

const ProcessStepOne = ({
  errors,
  userSession,
  handleSetErrors,
  selectedPocketId,
  documentUploaded,
  handleSetPocketId,
  handleCleanErrors,
  openAnalyzingModal,
  handleDialogAction,
  documentFileToUpload,
  setDocumentFileToUpload,
  handleProcessFileToUpload,
  handleDownloadAdamoPayFormat,
  handleRemoveDocumentFileToUpload
}: {
  errors: FormError
  selectedPocketId: string
  documentUploaded: boolean
  openAnalyzingModal: boolean
  userSession: UserSessionType
  handleCleanErrors: () => void
  handleDialogAction: () => void
  documentFileToUpload: Array<File>
  handleProcessFileToUpload: () => void
  handleDownloadAdamoPayFormat?: () => void
  handleSetErrors: (errors: FormError) => void
  handleSetPocketId: (pocketIdToSet: string) => void
  setDocumentFileToUpload: React.Dispatch<React.SetStateAction<Array<File>>>
  handleRemoveDocumentFileToUpload: (documentFilenameToDelete: string) => void
}) => {
  const { t } = useTranslation(['upload-document-process'])
  const { currentPocketsData, defaultPocketIdSelected } = useContext(AppStateContext)

  useEffect(() => {
    if ((userSession?.role === 'admin-client' || userSession?.role === 'admin-client-builder') && defaultPocketIdSelected) {
      handleSetPocketId(defaultPocketIdSelected)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession, defaultPocketIdSelected])

  return (
    <div
      className="upload-document-step-one-content"
      style={
        (config.environment !== 'production' &&
          (String(config.app.scienciatechCompanyId) === String(userSession.belongsTo) || String(config.app.demoScienciatechCompanyId) === String(userSession.belongsTo)) &&
          userSession?.role !== 'super-admin-client') ||
        (config.environment === 'production' && String(config.app.scienciatechCompanyId) === String(userSession.belongsTo) && userSession?.role !== 'super-admin-client')
          ? { height: '100%' }
          : {}
      }
    >
      <div className="upload-document-step-one-container">
        <h1 className="upload-document-step-one-title">{t('processStepOne.title')}</h1>
        <p>{t('processStepOne.mainInstruction')}</p>
        {userSession.role === 'super-admin-client' && Number(currentPocketsData?.length) > 0 && (
          <div style={{ width: '100%', marginTop: '15px' }}>
            <TextField
              select
              fullWidth
              variant="standard"
              value={selectedPocketId || ''}
              error={!!errors.selectedPocketId}
              helperText={errors.selectedPocketId}
              label={t('processStepOne.processWith')}
              onChange={(event) => handleSetPocketId(event.target.value)}
              sx={{ ...textFieldWhiteBg, '& .MuiInputBase-root .MuiSelect-select': { height: '2.7em', boxSizing: 'border-box', padding: '12px 16px 4px' } }}
            >
              <MenuItem
                disabled
                value=""
              >
                {t('processStepOne.select')}
              </MenuItem>
              {currentPocketsData?.map((pocketData: Pocket) => {
                return (
                  <MenuItem
                    key={String(pocketData.id)}
                    value={String(pocketData.id)}
                  >
                    {pocketData.name?.toUpperCase()}
                  </MenuItem>
                )
              })}
            </TextField>
          </div>
        )}
        <div className="upload-document-dropzone-container">
          <Dropzone
            handleClearErrors={handleCleanErrors}
            documentFileToUpload={documentFileToUpload}
            setDocumentFileToUpload={setDocumentFileToUpload}
            handleSetUploadError={(uploadErrors) => {
              if (uploadErrors[0].code === 'file-invalid-type') {
                handleSetErrors({ generalError: t('dropzone.errors.invalidFileFormat') })
              } else if (uploadErrors[0].code === 'file-too-large') {
                handleSetErrors({ generalError: t('dropzone.errors.fileSizeTooBig') })
              } else {
                handleSetErrors({ generalError: t('dropzone.errors.generalError') })
              }
            }}
          />
        </div>
        <div className="document-to-upload-file-list-container">
          {documentFileToUpload.map((file) => (
            <div
              key={file.name}
              className="document-to-upload-file-list-item"
            >
              <div className="upload-document-document-list-item-left-content">
                <div className="upload-document-document-icon-container">
                  <img
                    src={DocumentIcon}
                    alt="document-icon"
                  />
                </div>
                <div className="upload-document-document-name">{file.name}</div>
              </div>
              <Button
                className="button-delete-file"
                onClick={() => {
                  handleCleanErrors()
                  handleRemoveDocumentFileToUpload(file.name)
                }}
              >
                {t('buttonActionsTexts.delete')}
              </Button>
            </div>
          ))}
        </div>
        {errors.generalError && <div className="error-message">{errors.generalError}</div>}
        {userSession.role.indexOf('client') > -1 && (
          <div
            onClick={() => handleDownloadAdamoPayFormat?.()}
            className="upload-document-download-button-container"
          >
            <div className="upload-document-download-button-text">
              <span>{t('downloadFormat.clickHere')}</span> {t('downloadFormat.toDownload')}
            </div>
          </div>
        )}
        <div className={userSession.role.indexOf('client') > -1 ? 'load-document-action-button load-document-action-button-client' : 'load-document-action-button'}>
          <Button
            variant="contained"
            sx={baseButtonWhiteBg}
            onClick={handleProcessFileToUpload}
            disabled={userSession.role === 'super-admin-client' ? documentFileToUpload.length === 0 || !selectedPocketId : documentFileToUpload.length === 0}
          >
            {t('buttonActionsTexts.uploadDocument')}
          </Button>
        </div>
      </div>
      {openAnalyzingModal && (
        <Dialog
          hideCloseOption={true}
          actionButtonText={t('analyzingModal.actionButtonText')}
          hideActionsButtons={!documentUploaded}
          dialogTitle={t('analyzingModal.dialogTitle')}
          handleDialogAction={handleDialogAction}
        >
          <div className="document-to-upload-linear-progress-container">
            <LinearProgress />
          </div>
          {documentUploaded ? (
            <div className="document-to-upload-analyzing-modal-text">{t('analyzingModal.allowedToCloseWarning')}</div>
          ) : (
            <div className="document-to-upload-analyzing-modal-text">{t('analyzingModal.dontAllowToCloseWarning')}</div>
          )}
        </Dialog>
      )}
    </div>
  )
}

export default ProcessStepOne
